import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import FolderTree from './components/FolderTree';
import VaultToolbar from './components/VaultToolbar';
import CredentialItem from './components/CredentialItem';
import CreateItemModal from './components/CreateItemModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const VaultManagement = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [credentials, setCredentials] = useState([]);

  // Mock folder structure
  const [folders] = useState([
    {
      id: 'root',
      name: 'All Vaults',
      type: 'root',
      itemCount: 47,
      children: [
        {
          id: 'engineering',
          name: 'Engineering',
          type: 'department',
          itemCount: 23,
          children: [
            { id: 'dev-tools', name: 'Development Tools', type: 'vault', itemCount: 8 },
            { id: 'staging', name: 'Staging Environment', type: 'vault', itemCount: 6 },
            { id: 'production', name: 'Production', type: 'vault', itemCount: 9 }
          ]
        },
        {
          id: 'marketing',
          name: 'Marketing',
          type: 'department',
          itemCount: 12,
          children: [
            { id: 'social-media', name: 'Social Media', type: 'vault', itemCount: 5 },
            { id: 'analytics', name: 'Analytics Tools', type: 'vault', itemCount: 4 },
            { id: 'campaigns', name: 'Campaign Tools', type: 'vault', itemCount: 3 }
          ]
        },
        {
          id: 'finance',
          name: 'Finance',
          type: 'department',
          itemCount: 8,
          children: [
            { id: 'banking', name: 'Banking', type: 'vault', itemCount: 4 },
            { id: 'accounting', name: 'Accounting Software', type: 'vault', itemCount: 4 }
          ]
        },
        {
          id: 'personal',
          name: 'Personal Vault',
          type: 'vault',
          itemCount: 4
        }
      ]
    }
  ]);

  // Mock credentials data
  const mockCredentials = [
    {
      id: '1',
      name: 'GitHub Enterprise',
      type: 'password',
      url: 'https://github.com/company',
      username: 'admin@company.com',
      password: 'Gh$tr0ng_P@ssw0rd123!',
      passwordStrength: 'strong',
      lastModified: new Date(Date.now() - 86400000)?.toISOString(),
      tags: ['development', 'critical'],
      isFavorite: true,
      isShared: true,
      hasAttachments: false,
      breachStatus: false,
      needsRotation: false,
      folderId: 'dev-tools'
    },
    {
      id: '2',
      name: 'AWS Production Console',
      type: 'password',
      url: 'https://console.aws.amazon.com',
      username: 'prod-admin',
      password: 'AWS_Pr0d_S3cur3_2024!',
      passwordStrength: 'strong',
      lastModified: new Date(Date.now() - 172800000)?.toISOString(),
      tags: ['production', 'critical', 'aws'],
      isFavorite: true,
      isShared: false,
      hasAttachments: true,
      breachStatus: false,
      needsRotation: false,
      folderId: 'production'
    },
    {
      id: '3',
      name: 'Stripe API Key',
      type: 'api-key',
      url: 'https://api.stripe.com',
      username: '',
      password: 'sk_live_51H7xYzKj9vRm2N8X3qP4wQ5r6T7u8V9w0A1b2C3d4E5f6G7h8I9j0K1l2M3n4O5p6Q7r8S9t0U1v2W3x4Y5z6',
      passwordStrength: null,
      lastModified: new Date(Date.now() - 259200000)?.toISOString(),
      tags: ['payment', 'production', 'api'],
      isFavorite: false,
      isShared: true,
      hasAttachments: false,
      breachStatus: false,
      needsRotation: false,
      folderId: 'production'
    },
    {
      id: '4',
      name: 'Database Connection',
      type: 'database',
      url: 'postgresql://prod-db.company.com:5432/maindb',
      username: 'db_admin',
      password: 'DB_S3cur3_C0nn3ct!0n',
      passwordStrength: 'strong',
      lastModified: new Date(Date.now() - 345600000)?.toISOString(),
      tags: ['database', 'production'],
      isFavorite: false,
      isShared: false,
      hasAttachments: false,
      breachStatus: false,
      needsRotation: true,
      folderId: 'production'
    },
    {
      id: '5',
      name: 'Social Media Manager',
      type: 'password',
      url: 'https://hootsuite.com',
      username: 'marketing@company.com',
      password: 'S0c!al_M3d!a_2024',
      passwordStrength: 'medium',
      lastModified: new Date(Date.now() - 432000000)?.toISOString(),
      tags: ['social-media', 'marketing'],
      isFavorite: false,
      isShared: true,
      hasAttachments: false,
      breachStatus: true,
      needsRotation: true,
      folderId: 'social-media'
    },
    {
      id: '6',
      name: 'Google Analytics',
      type: 'password',
      url: 'https://analytics.google.com',
      username: 'analytics@company.com',
      password: 'An@lyt!cs_2024_Secure',
      passwordStrength: 'strong',
      lastModified: new Date(Date.now() - 518400000)?.toISOString(),
      tags: ['analytics', 'marketing'],
      isFavorite: false,
      isShared: false,
      hasAttachments: false,
      breachStatus: false,
      needsRotation: false,
      folderId: 'analytics'
    },
    {
      id: '7',
      name: 'SSH Server Access',
      type: 'ssh-key',
      url: 'server.company.com',
      username: 'root',
      password: '-----BEGIN PRIVATE KEY-----\nMIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQC...',
      passwordStrength: null,
      lastModified: new Date(Date.now() - 604800000)?.toISOString(),
      tags: ['server', 'ssh', 'critical'],
      isFavorite: false,
      isShared: false,
      hasAttachments: true,
      breachStatus: false,
      needsRotation: false,
      folderId: 'dev-tools'
    },
    {
      id: '8',
      name: 'Banking Portal',
      type: 'password',
      url: 'https://business.chase.com',
      username: 'finance@company.com',
      password: 'B@nk!ng_S3cur3_2024!',
      passwordStrength: 'strong',
      lastModified: new Date(Date.now() - 691200000)?.toISOString(),
      tags: ['banking', 'finance', 'critical'],
      isFavorite: true,
      isShared: false,
      hasAttachments: false,
      breachStatus: false,
      needsRotation: false,
      folderId: 'banking'
    }
  ];

  useEffect(() => {
    setCredentials(mockCredentials);
    // Set default selected folder to root
    setSelectedFolder(folders?.[0]);
  }, []);

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    setSelectedItems([]);
  };

  const handleItemSelect = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems?.filter(id => id !== itemId));
    }
  };

  const handleBulkAction = (action) => {
    console.log('Bulk action:', action, 'on items:', selectedItems);
    // Implement bulk actions
    switch (action) {
      case 'move': console.log('Moving items to folder');
        break;
      case 'tag': console.log('Adding tags to items');
        break;
      case 'share': navigate('/sharing-management');
        break;
      case 'export':
        console.log('Exporting items');
        break;
      case 'delete':
        console.log('Deleting items');
        setCredentials(credentials?.filter(item => !selectedItems?.includes(item?.id)));
        setSelectedItems([]);
        break;
      default:
        break;
    }
  };

  const handleCreateItem = () => {
    setShowCreateModal(true);
  };

  const handleSaveItem = (newItem) => {
    setCredentials([...credentials, newItem]);
  };

  const handleEditItem = (item) => {
    console.log('Editing item:', item);
    // Navigate to edit modal or inline edit
  };

  const handleShareItem = (item) => {
    console.log('Sharing item:', item);
    navigate('/sharing-management');
  };

  const handleCopyItem = (field, value) => {
    console.log(`Copied ${field}:`, value);
  };

  const handleCreateFolder = () => {
    console.log('Creating new folder');
  };

  // Filter credentials based on selected folder and search query
  const filteredCredentials = credentials?.filter(item => {
    const matchesFolder = !selectedFolder || 
      selectedFolder?.id === 'root' || 
      item?.folderId === selectedFolder?.id ||
      (selectedFolder?.children && selectedFolder?.children?.some(child => 
        child?.id === item?.folderId || 
        (child?.children && child?.children?.some(grandchild => grandchild?.id === item?.folderId))
      ));
    
    const matchesSearch = !searchQuery || 
      item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.url?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.username?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()));

    return matchesFolder && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
      } pt-16`}>
        <div className="flex h-[calc(100vh-4rem)]">
          {/* Folder Tree Panel */}
          <div className="hidden lg:block w-80 border-r border-border">
            <FolderTree
              folders={folders}
              selectedFolder={selectedFolder}
              onFolderSelect={handleFolderSelect}
              onCreateFolder={handleCreateFolder}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {/* Toolbar */}
            <VaultToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedItems={selectedItems}
              onBulkAction={handleBulkAction}
              onCreateItem={handleCreateItem}
              onViewToggle={setViewMode}
              viewMode={viewMode}
            />

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto">
              {filteredCredentials?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                    <Icon name="Lock" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {searchQuery ? 'No items found' : 'No items in this vault'}
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-md">
                    {searchQuery 
                      ? 'Try adjusting your search terms or filters to find what you\'re looking for.'
                      : 'Get started by adding your first credential item to this vault.'
                    }
                  </p>
                  <Button
                    variant="default"
                    onClick={handleCreateItem}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Add First Item
                  </Button>
                </div>
              ) : (
                <div className={viewMode === 'grid' ? 'p-6' : ''}>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredCredentials?.map(item => (
                        <CredentialItem
                          key={item?.id}
                          item={item}
                          isSelected={selectedItems?.includes(item?.id)}
                          onSelect={handleItemSelect}
                          onEdit={handleEditItem}
                          onShare={handleShareItem}
                          onCopy={handleCopyItem}
                          viewMode={viewMode}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {filteredCredentials?.map(item => (
                        <CredentialItem
                          key={item?.id}
                          item={item}
                          isSelected={selectedItems?.includes(item?.id)}
                          onSelect={handleItemSelect}
                          onEdit={handleEditItem}
                          onShare={handleShareItem}
                          onCopy={handleCopyItem}
                          viewMode={viewMode}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Folder Tree */}
        <div className="lg:hidden">
          {/* Mobile folder navigation would be implemented here */}
        </div>
      </main>
      {/* Create Item Modal */}
      <CreateItemModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleSaveItem}
        selectedFolder={selectedFolder}
      />
    </div>
  );
};

export default VaultManagement;